import React, { useState } from "react";
import { API_BASE_URL, assertLlmRuntimeReady, extractApiError, startBatch } from "../services/api";
import { BatchResponse } from "../types";

// Generated by GitHub Copilot
/**
 * Returns true when the API error indicates LLM configuration is missing/unavailable.
 */
const isLlmNotConfiguredError = (message: string): boolean =>
  /llm\s+not\s+configured|llm\s+unavailable|preflight failed|virtual network|403 forbidden|401 unauthorized|endpoint/i.test(message);

const BatchPage: React.FC = () => {
  const [blobFolder, setBlobFolder]       = useState("");
  const [loading, setLoading]             = useState(false);
  const [result, setResult]               = useState<BatchResponse | null>(null);
  const [error, setError]                 = useState<string | null>(null);
  const [downloading, setDownloading]     = useState(false);
  const [pushing, setPushing]             = useState(false);
  const [pushSuccess, setPushSuccess]     = useState(false);
  const [actionError, setActionError]     = useState<string | null>(null);

  const handleStart = async () => {
    if (!blobFolder.trim()) {
      setError("Please enter a Blob folder name.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await assertLlmRuntimeReady();
      const data = await startBatch(blobFolder);
      setResult(data);
    } catch (err) {
      const message = extractApiError(err, "Batch scan failed. Please check the folder path.");
      setError(message);
      if (isLlmNotConfiguredError(message)) {
        alert(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.thread_id) return;
    setDownloading(true);
    setActionError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/batch/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ thread_id: result.thread_id, output_format: "general", file_type: "excel" }),
      });
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const disposition = response.headers.get("content-disposition") || "";
      const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n;]+)\1/);
      const serverFilename = match ? match[2].trim() : "batch_output.xlsx";
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = serverFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setActionError("Download failed: " + (err?.message || "Please try again."));
    } finally {
      setDownloading(false);
    }
  };

  const handlePushToAlation = async () => {
    if (!result?.thread_id) return;
    setPushing(true);
    setPushSuccess(false);
    setActionError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/batch/push`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ thread_id: result.thread_id, target_platform: "alation" }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error: ${response.status}`);
      }
      setPushSuccess(true);
    } catch (err: any) {
      setActionError("Push to Alation failed: " + (err?.message || "Please try again."));
    } finally {
      setPushing(false);
    }
  };

  const reset = () => {
    setBlobFolder("");
    setResult(null);
    setError(null);
    setActionError(null);
    setPushSuccess(false);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>🗂 Batch File Scan</h2>

      {/* Input Section */}
      {!result && (
        <div style={styles.inputBox}>
          <label style={styles.label}>Enter Blob Folder Name:</label>
          <input
            type="text"
            value={blobFolder}
            onChange={(e) => setBlobFolder(e.target.value)}
            placeholder="Input_UserName"
            style={styles.input}
          />
          <button
            onClick={handleStart}
            style={styles.startBtn}
            disabled={loading}
          >
            {loading ? "⏳ Scanning..." : "🚀 Start Batch Scan"}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={styles.errorBox}>
          <p>❌ {error}</p>
          <button onClick={reset} style={styles.resetBtn}>Try Again</button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={styles.resultBox}>
          <h3 style={{ marginTop: 0 }}>✅ Batch Scan Complete!</h3>
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Status:</span>
            <span style={styles.badge}>{result.status}</span>
          </div>
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Folder Scanned:</span>
            <span style={{ fontSize: 13 }}>{result.folder}</span>
          </div>

          {actionError && (
            <div style={styles.actionError}>{actionError}</div>
          )}

          {pushSuccess && (
            <div style={styles.pushSuccess}>✅ Successfully pushed to Alation!</div>
          )}

          <div style={styles.actionRow}>
            <button
              onClick={handleDownload}
              style={{ ...styles.downloadBtn, opacity: downloading ? 0.7 : 1 }}
              disabled={downloading}
            >
              {downloading ? "⏳ Downloading..." : "📥 Download Excel"}
            </button>
            <button
              onClick={handlePushToAlation}
              style={{ ...styles.pushBtn, ...styles.disabledPushBtn, opacity: pushing ? 0.7 : 1 }}
              disabled={pushing || pushSuccess || result !== null}
            >
              {pushing ? "⏳ Pushing..." : pushSuccess ? "✅ Pushed to Alation" : "🚀 Push to Alation"}
            </button>
          </div>

          <button onClick={reset} style={styles.resetBtn}>
            Start Another Scan
          </button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container:   { maxWidth: 700, margin: "0 auto", padding: 32 },
  title:       { fontSize: 24, marginBottom: 24, color: "#333" },
  inputBox:    { background: "#fff", border: "1px solid #ddd", borderRadius: 10, padding: 24 },
  label:       { fontWeight: "bold", display: "block", marginBottom: 8, color: "#555" },
  input:       { width: "100%", padding: "10px 14px", fontSize: 15, borderRadius: 6, border: "1px solid #ccc", marginBottom: 16, boxSizing: "border-box" },
  startBtn:    { padding: "12px 24px", background: "#1565c0", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15 },
  resultBox:   { background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 10, padding: 24, marginTop: 16 },
  metaRow:     { display: "flex", gap: 12, marginBottom: 10, alignItems: "center" },
  metaLabel:   { fontWeight: "bold", minWidth: 130, color: "#555" },
  badge:       { background: "#c8e6c9", color: "#2e7d32", padding: "2px 10px", borderRadius: 12, fontWeight: "bold" },
  errorBox:    { background: "#ffebee", border: "1px solid #ef9a9a", borderRadius: 10, padding: 24, marginTop: 16 },
  actionRow:   { display: "flex", gap: 12, marginTop: 20, marginBottom: 16, flexWrap: "wrap" },
  downloadBtn: { padding: "12px 24px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: "bold" },
  pushBtn:     { padding: "12px 24px", background: "#1565c0", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: "bold" },
  disabledPushBtn: { background: "#9e9e9e", cursor: "not-allowed", opacity: 0.65 },
  resetBtn:    { padding: "10px 20px", background: "#f57c00", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", marginTop: 4 },
  actionError: { background: "#ffebee", border: "1px solid #ef9a9a", borderRadius: 6, padding: "10px 14px", color: "#c62828", marginBottom: 12, fontSize: 14 },
  pushSuccess: { background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 6, padding: "10px 14px", color: "#2e7d32", marginBottom: 12, fontSize: 14, fontWeight: "bold" },
};

export default BatchPage;
